import React, { useEffect, useState, useRef, useCallback } from 'react';
import { FaPlay, FaSave } from 'react-icons/fa';
import { Button } from '../../ComponnentsUI/common/Button';
import { Select } from '../../ComponnentsUI/common/Select';

interface FeedPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;

  onStartExam: (updatedConfig?: any) => void;
  onCropChange?: (updatedConfig: any) => void;
  videoRef?: React.RefObject<HTMLVideoElement>;
  videoStream?: MediaStream | null;
  machineConfig: any;
  feedType: string;
  autoStartDelay?: number;
  machinesList?: any[];
}

const FeedPreviewModal: React.FC<FeedPreviewModalProps> = ({
  isOpen,
  onClose,
  onStartExam,
  onCropChange,
  videoRef: externalVideoRef,
  videoStream,
  machineConfig,
  feedType,
  autoStartDelay = 3,
  machinesList = [],
}) => {
  const localVideoRef = React.useRef<HTMLVideoElement>(null);
  const videoRef = externalVideoRef || localVideoRef;
  const hasInitialized = useRef(false);
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const isResizing = useRef(false);
  const resizeHandle = useRef<string>('');
  const dragStart = useRef({ x: 0, y: 0 });

  const [countdown, setCountdown] = useState(autoStartDelay);
  const [isCountdownPaused, setIsCountdownPaused] = useState(false);
  const [cropData, setCropData] = useState<any>(null);
  const [localMachineConfig, setLocalMachineConfig] = useState(machineConfig);
  const [videoSize, setVideoSize] = useState({ width: 0, height: 0 });
  const [videoMetrics, setVideoMetrics] = useState<{
    renderedWidth: number;
    renderedHeight: number;
    offsetX: number;
    offsetY: number;
  } | null>(null);

  const [cropBox, setCropBox] = useState({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });
  const [isInteracting, setIsInteracting] = useState(false);

  useEffect(() => {
    setLocalMachineConfig(machineConfig);
    if (machineConfig && machineConfig.crop_x != null) {
      const newCropData = {
        x: machineConfig.crop_x,
        y: machineConfig.crop_y,
        width: machineConfig.crop_width,
        height: machineConfig.crop_height,
      };
      setCropData(newCropData);
      setCropBox(newCropData);
    }
  }, [machineConfig]);

  useEffect(() => {
    if (isOpen && videoStream && videoRef.current) {
      console.log('[FeedPreviewModal] Setting video stream', {
        videoStream,
        active: videoStream.active,
        tracks: videoStream.getTracks().map((t) => ({
          kind: t.kind,
          enabled: t.enabled,
          readyState: t.readyState,
        })),
        hasVideo: !!videoRef.current,
      });

      videoRef.current.srcObject = videoStream;

      const playVideo = async () => {
        try {
          if (videoRef.current) {
            await videoRef.current.play();
            console.log('[FeedPreviewModal] Video playing successfully');
          }
        } catch (err) {
          if (err.name !== 'AbortError') {
            console.error('[FeedPreviewModal] Error playing video:', err);
          } else {
            console.log(
              '[FeedPreviewModal] Play request interrupted (AbortError), likely harmless'
            );
          }
        }
      };

      playVideo();
    } else if (isOpen) {
      console.warn('[FeedPreviewModal] Open but missing requirements:', {
        isOpen,
        hasStream: !!videoStream,
        hasRef: !!videoRef.current,
      });
    }
  }, [isOpen, videoStream, videoRef]);

  // Reset countdown and crop state when modal opens
  useEffect(() => {
    if (isOpen) {
      hasInitialized.current = false;
      setCountdown(autoStartDelay);
      setIsCountdownPaused(false);

      // Initialize crop data from machine config when modal opens
      if (machineConfig && machineConfig.crop_x != null) {
        const initialCrop = {
          x: machineConfig.crop_x,
          y: machineConfig.crop_y,
          width: machineConfig.crop_width,
          height: machineConfig.crop_height,
        };
        setCropData(initialCrop);
        setCropBox(initialCrop);
      }
    } else {
      hasInitialized.current = false;
    }
  }, [isOpen, autoStartDelay, machineConfig]);

  // Mark as initialized after reset completes
  useEffect(() => {
    if (isOpen && countdown === autoStartDelay) {
      hasInitialized.current = true;
    }
  }, [isOpen, countdown, autoStartDelay]);

  // Countdown timer
  useEffect(() => {
    if (!isOpen || !hasInitialized.current || isCountdownPaused) return;

    if (countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0) {
      console.log('[FeedPreviewModal] Auto-starting exam');
      handleStartNow();
    }
  }, [isOpen, countdown, isCountdownPaused]);

  // Track video size and initialize crop box
  useEffect(() => {
    const video = videoRef.current;
    const container = videoContainerRef.current;
    if (!video || !isOpen || !container) return;

    const updateVideoMetrics = () => {
      if (video.videoWidth > 0 && video.videoHeight > 0 && container) {
        const containerRect = container.getBoundingClientRect();

        // Skip if container has no size yet
        if (containerRect.width === 0 || containerRect.height === 0) return;

        // Calculate the actual rendered video dimensions within the container
        const videoAspect = video.videoWidth / video.videoHeight;
        const containerAspect = containerRect.width / containerRect.height;

        let renderedWidth, renderedHeight, offsetX, offsetY;

        if (containerAspect > videoAspect) {
          // Container is wider - video will have black bars on left/right
          renderedHeight = containerRect.height;
          renderedWidth = renderedHeight * videoAspect;
          offsetX = (containerRect.width - renderedWidth) / 2;
          offsetY = 0;
        } else {
          // Container is taller - video will have black bars on top/bottom
          renderedWidth = containerRect.width;
          renderedHeight = renderedWidth / videoAspect;
          offsetX = 0;
          offsetY = (containerRect.height - renderedHeight) / 2;
        }

        setVideoMetrics({
          renderedWidth,
          renderedHeight,
          offsetX,
          offsetY,
        });

        setVideoSize({
          width: video.videoWidth,
          height: video.videoHeight,
        });

        // Initialize crop box from machine config
        if (
          machineConfig &&
          machineConfig.crop_x != null &&
          cropBox.width === 0
        ) {
          setCropBox({
            x: machineConfig.crop_x,
            y: machineConfig.crop_y,
            width: machineConfig.crop_width,
            height: machineConfig.crop_height,
          });
        }
      }
    };

    // Use ResizeObserver to track container size changes
    const resizeObserver = new ResizeObserver(() => {
      // Small delay to ensure layout has settled
      requestAnimationFrame(updateVideoMetrics);
    });
    resizeObserver.observe(container);

    video.addEventListener('loadedmetadata', updateVideoMetrics);
    video.addEventListener('playing', updateVideoMetrics);
    window.addEventListener('resize', updateVideoMetrics);

    // Check immediately if video is already loaded
    if (video.readyState >= 2) {
      // Delay initial check to allow flex layout to settle
      requestAnimationFrame(() => {
        setTimeout(updateVideoMetrics, 50);
      });
    }

    return () => {
      resizeObserver.disconnect();
      video.removeEventListener('loadedmetadata', updateVideoMetrics);
      video.removeEventListener('playing', updateVideoMetrics);
      window.removeEventListener('resize', updateVideoMetrics);
    };
  }, [isOpen, videoRef, machineConfig, cropBox.width]);

  const getEventCoordinates = useCallback(
    (e: MouseEvent | TouchEvent | React.MouseEvent | React.TouchEvent) => {
      if ('touches' in e && e.touches.length > 0) {
        return { x: e.touches[0].clientX, y: e.touches[0].clientY };
      } else if ('changedTouches' in e && e.changedTouches.length > 0) {
        return { x: e.changedTouches[0].clientX, y: e.changedTouches[0].clientY };
      } else if ('clientX' in e) {
        return { x: e.clientX, y: e.clientY };
      }
      return { x: 0, y: 0 };
    },
    []
  );

  const handlePointerDown = useCallback(
    (e: MouseEvent | TouchEvent | React.MouseEvent | React.TouchEvent, handle?: string) => {
      e.preventDefault();
      e.stopPropagation();

      // Stop countdown when user starts interacting with crop
      setIsCountdownPaused(true);
      setIsInteracting(true);

      if (handle) {
        isResizing.current = true;
        resizeHandle.current = handle;
      } else {
        isDragging.current = true;
      }

      const coords = getEventCoordinates(e);
      dragStart.current = {
        x: coords.x,
        y: coords.y,
      };
    },
    [getEventCoordinates]
  );

  const handlePointerMove = useCallback(
    (e: MouseEvent | TouchEvent) => {
      if (!isDragging.current && !isResizing.current) return;
      if (!videoContainerRef.current || !videoRef.current || !videoMetrics)
        return;

      if ('touches' in e) {
        e.preventDefault();
      }

      const video = videoRef.current;
      const videoWidth = video.videoWidth;
      const videoHeight = video.videoHeight;

      // Calculate scale factor between rendered video and actual video dimensions
      const scaleX = videoWidth / videoMetrics.renderedWidth;
      const scaleY = videoHeight / videoMetrics.renderedHeight;

      const coords = getEventCoordinates(e);
      const deltaX = (coords.x - dragStart.current.x) * scaleX;
      const deltaY = (coords.y - dragStart.current.y) * scaleY;

      dragStart.current = { x: coords.x, y: coords.y };

      const minSize = 50;

      if (isDragging.current) {
        // Dragging - move the crop box
        setCropBox((prev) => {
          const newX = Math.max(
            0,
            Math.min(prev.x + deltaX, videoWidth - prev.width)
          );
          const newY = Math.max(
            0,
            Math.min(prev.y + deltaY, videoHeight - prev.height)
          );
          return { ...prev, x: newX, y: newY };
        });
      } else if (isResizing.current) {
        // Resizing - adjust crop box size with proper bounds
        setCropBox((prev) => {
          let { x, y, width, height } = prev;
          const handle = resizeHandle.current;

          const rightEdge = x + width;
          const bottomEdge = y + height;

          if (handle === 'e' || handle.includes('e')) {
            const newWidth = width + deltaX;
            width = Math.max(minSize, Math.min(newWidth, videoWidth - x));
          }

          if (handle === 'w' || handle.includes('w')) {
            const newX = x + deltaX;
            if (newX >= 0 && rightEdge - newX >= minSize) {
              x = newX;
              width = rightEdge - newX;
            } else if (newX < 0) {
              x = 0;
              width = rightEdge;
            } else {
              // Hit minimum size - right edge stays fixed
              x = rightEdge - minSize;
              width = minSize;
            }
          }

          // South edge - only change height (top edge stays fixed)
          if (handle === 's' || handle.includes('s')) {
            const newHeight = height + deltaY;
            height = Math.max(minSize, Math.min(newHeight, videoHeight - y));
          }

          // North edge - only change y and height (bottom edge stays fixed)
          if (handle === 'n' || handle.includes('n')) {
            const newY = y + deltaY;
            if (newY >= 0 && bottomEdge - newY >= minSize) {
              y = newY;
              height = bottomEdge - newY;
            } else if (newY < 0) {
              y = 0;
              height = bottomEdge;
            } else {
              // Hit minimum size - bottom edge stays fixed
              y = bottomEdge - minSize;
              height = minSize;
            }
          }

          // Final safety clamp to ensure we're within bounds
          x = Math.max(0, Math.min(x, videoWidth - minSize));
          y = Math.max(0, Math.min(y, videoHeight - minSize));
          width = Math.max(minSize, Math.min(width, videoWidth - x));
          height = Math.max(minSize, Math.min(height, videoHeight - y));

          return { x, y, width, height };
        });
      }
    },
    [videoRef, videoMetrics, getEventCoordinates]
  );

  const handlePointerUp = useCallback(() => {
    isDragging.current = false;
    isResizing.current = false;
    resizeHandle.current = '';
    setIsInteracting(false);
  }, []);

  // Add global mouse and touch event listeners
  useEffect(() => {
    if (isOpen) {
      // Mouse events
      document.addEventListener('mousemove', handlePointerMove);
      document.addEventListener('mouseup', handlePointerUp);
      // Touch events (passive: false to allow preventDefault for scroll prevention)
      document.addEventListener('touchmove', handlePointerMove, { passive: false });
      document.addEventListener('touchend', handlePointerUp);
      document.addEventListener('touchcancel', handlePointerUp);
      return () => {
        document.removeEventListener('mousemove', handlePointerMove);
        document.removeEventListener('mouseup', handlePointerUp);
        document.removeEventListener('touchmove', handlePointerMove);
        document.removeEventListener('touchend', handlePointerUp);
        document.removeEventListener('touchcancel', handlePointerUp);
      };
    }
  }, [isOpen, handlePointerMove, handlePointerUp]);

  const handleSaveCrop = () => {
    // Update local machine config with current crop box values
    const updatedConfig = {
      ...localMachineConfig,
      crop_x: Math.round(cropBox.x),
      crop_y: Math.round(cropBox.y),
      crop_width: Math.round(cropBox.width),
      crop_height: Math.round(cropBox.height),
    };
    setLocalMachineConfig(updatedConfig);
    setCropData({
      x: Math.round(cropBox.x),
      y: Math.round(cropBox.y),
      width: Math.round(cropBox.width),
      height: Math.round(cropBox.height),
    });

    if (onCropChange) {
      onCropChange(updatedConfig);
    }
  };

  const handleStartNow = async () => {
    hasInitialized.current = false;
    // Pass the updated config and let the parent handle closing and starting
    await onStartExam(localMachineConfig);
    // Close modal after callback completes
    onClose();
  };

  if (!isOpen || !machineConfig) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm p-2 sm:p-4 md:p-6">
      <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-2xl flex flex-col w-full h-full sm:h-auto sm:max-h-[95vh] md:max-h-[90vh] max-w-6xl overflow-hidden">
        {/* Header - Compact */}
        <div className="flex items-center justify-between p-2 sm:p-3 md:p-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <h2 className="text-base sm:text-lg md:text-xl font-bold text-gray-900 dark:text-white">
            Adjust Crop Area
          </h2>
          {!isCountdownPaused && (
            <div className="flex items-center gap-2 px-2 sm:px-3 md:px-4 py-1 sm:py-1.5 md:py-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <span className="text-xs sm:text-sm font-medium text-blue-700 dark:text-blue-300">
                Starting in
              </span>
              <span className="text-xl sm:text-2xl md:text-3xl font-bold text-blue-600 dark:text-blue-400 tabular-nums">
                {countdown}
              </span>
            </div>
          )}
          <button
            onClick={onClose}
            className="p-1.5 rounded-full text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ml-2"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Video Preview - Takes maximum space */}
        <div className="flex-1 flex flex-col items-center justify-center min-h-0 p-2 sm:p-3 md:p-4">
          <div
            ref={videoContainerRef}
            className="relative bg-black rounded-lg select-none w-full overflow-visible"
            style={{
              aspectRatio: '16/9',
              maxHeight: 'calc(90vh - 180px)',
            }}
          >
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="absolute inset-0 w-full h-full object-contain"
            />

            {/* Interactive Crop Box */}
            {videoRef.current &&
              videoRef.current.videoWidth > 0 &&
              cropBox.width > 0 &&
              videoMetrics && (
                <div
                  className={`absolute cursor-move touch-none transition-all duration-150 overflow-visible ${
                    isInteracting
                      ? 'border-[3px] border-blue-500 bg-blue-500/20'
                      : 'border-2 border-green-500 bg-green-500/10'
                  }`}
                  style={{
                    left: `${videoMetrics.offsetX + (cropBox.x / videoRef.current.videoWidth) * videoMetrics.renderedWidth}px`,
                    top: `${videoMetrics.offsetY + (cropBox.y / videoRef.current.videoHeight) * videoMetrics.renderedHeight}px`,
                    width: `${(cropBox.width / videoRef.current.videoWidth) * videoMetrics.renderedWidth}px`,
                    height: `${(cropBox.height / videoRef.current.videoHeight) * videoMetrics.renderedHeight}px`,
                  }}
                  onMouseDown={(e) => handlePointerDown(e)}
                  onTouchStart={(e) => handlePointerDown(e)}
                >
                  {/* Crop info label */}
                  <div
                    className={`absolute -top-10 left-1/2 -translate-x-1/2 text-white text-xs sm:text-sm font-semibold px-3 py-1.5 rounded-lg shadow-lg whitespace-nowrap z-20 transition-colors ${
                      isInteracting ? 'bg-blue-600' : 'bg-green-600'
                    }`}
                  >
                    {Math.round(cropBox.width)} × {Math.round(cropBox.height)}
                  </div>

                  {/* Corner resize handles - Large touch-friendly circles */}
                  {/* Top-Left */}
                  <div
                    className={`absolute z-20 touch-none transition-all duration-150 ${
                      isInteracting ? 'bg-blue-500 scale-110' : 'bg-green-500'
                    }`}
                    style={{
                      top: '-16px',
                      left: '-16px',
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      border: '3px solid white',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                      cursor: 'nw-resize',
                    }}
                    onMouseDown={(e) => handlePointerDown(e, 'nw')}
                    onTouchStart={(e) => handlePointerDown(e, 'nw')}
                  />
                  {/* Top-Right */}
                  <div
                    className={`absolute z-20 touch-none transition-all duration-150 ${
                      isInteracting ? 'bg-blue-500 scale-110' : 'bg-green-500'
                    }`}
                    style={{
                      top: '-16px',
                      right: '-16px',
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      border: '3px solid white',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                      cursor: 'ne-resize',
                    }}
                    onMouseDown={(e) => handlePointerDown(e, 'ne')}
                    onTouchStart={(e) => handlePointerDown(e, 'ne')}
                  />
                  {/* Bottom-Left */}
                  <div
                    className={`absolute z-20 touch-none transition-all duration-150 ${
                      isInteracting ? 'bg-blue-500 scale-110' : 'bg-green-500'
                    }`}
                    style={{
                      bottom: '-16px',
                      left: '-16px',
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      border: '3px solid white',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                      cursor: 'sw-resize',
                    }}
                    onMouseDown={(e) => handlePointerDown(e, 'sw')}
                    onTouchStart={(e) => handlePointerDown(e, 'sw')}
                  />
                  {/* Bottom-Right */}
                  <div
                    className={`absolute z-20 touch-none transition-all duration-150 ${
                      isInteracting ? 'bg-blue-500 scale-110' : 'bg-green-500'
                    }`}
                    style={{
                      bottom: '-16px',
                      right: '-16px',
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      border: '3px solid white',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                      cursor: 'se-resize',
                    }}
                    onMouseDown={(e) => handlePointerDown(e, 'se')}
                    onTouchStart={(e) => handlePointerDown(e, 'se')}
                  />

                  {/* Edge resize zones - invisible but large touch targets */}
                  {/* Top edge */}
                  <div
                    className="absolute touch-none"
                    style={{
                      top: '-20px',
                      left: '32px',
                      right: '32px',
                      height: '40px',
                      cursor: 'n-resize',
                    }}
                    onMouseDown={(e) => handlePointerDown(e, 'n')}
                    onTouchStart={(e) => handlePointerDown(e, 'n')}
                  />
                  {/* Bottom edge */}
                  <div
                    className="absolute touch-none"
                    style={{
                      bottom: '-20px',
                      left: '32px',
                      right: '32px',
                      height: '40px',
                      cursor: 's-resize',
                    }}
                    onMouseDown={(e) => handlePointerDown(e, 's')}
                    onTouchStart={(e) => handlePointerDown(e, 's')}
                  />
                  {/* Left edge */}
                  <div
                    className="absolute touch-none"
                    style={{
                      left: '-20px',
                      top: '32px',
                      bottom: '32px',
                      width: '40px',
                      cursor: 'w-resize',
                    }}
                    onMouseDown={(e) => handlePointerDown(e, 'w')}
                    onTouchStart={(e) => handlePointerDown(e, 'w')}
                  />
                  {/* Right edge */}
                  <div
                    className="absolute touch-none"
                    style={{
                      right: '-20px',
                      top: '32px',
                      bottom: '32px',
                      width: '40px',
                      cursor: 'e-resize',
                    }}
                    onMouseDown={(e) => handlePointerDown(e, 'e')}
                    onTouchStart={(e) => handlePointerDown(e, 'e')}
                  />
                </div>
              )}
          </div>
        </div>

        {/* Footer - Compact with info & actions */}
        <div className="shrink-0 border-t border-gray-200 dark:border-gray-700 p-2 sm:p-3 md:p-4 bg-gray-50 dark:bg-gray-800/50">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-4">
            {/* Configuration Select */}
            <div className="flex-1 flex flex-col sm:flex-row gap-2 sm:gap-4">
              <div className="flex-1 min-w-0">
                <label className="block text-[10px] sm:text-xs font-semibold text-gray-600 dark:text-gray-400 mb-0.5 sm:mb-1">
                  Configuration
                </label>
                <Select
                  className="w-full px-2 sm:px-3 py-1.5 sm:py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md text-xs sm:text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={localMachineConfig?.id || ''}
                  onFocus={() => setIsCountdownPaused(true)}
                  onChange={(e) => {
                    const selectedId = e.target.value;
                    const selectedMachine = machinesList.find(
                      (m) => m.id.toString() === selectedId
                    );
                    if (selectedMachine) {
                      setIsCountdownPaused(true);
                      setLocalMachineConfig(selectedMachine);
                      if (selectedMachine.crop_x != null) {
                        const newCropData = {
                          x: selectedMachine.crop_x,
                          y: selectedMachine.crop_y,
                          width: selectedMachine.crop_width,
                          height: selectedMachine.crop_height,
                        };
                        setCropData(newCropData);
                        setCropBox(newCropData);

                        if (onCropChange) {
                          onCropChange(selectedMachine);
                        }
                      }
                    }
                  }}
                  options={machinesList.map((machine) => ({
                    value: String(machine.id),
                    label: `${machine.name} (${machine.crop_width}x${machine.crop_height})`,
                  }))}
                />
              </div>

              <div className="shrink-0">
                <label className="block text-[10px] sm:text-xs font-semibold text-gray-600 dark:text-gray-400 mb-0.5 sm:mb-1">
                  Feed Source
                </label>
                <div className="px-2 sm:px-3 py-1.5 sm:py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md text-xs sm:text-sm text-gray-900 dark:text-white">
                  {feedType === 'USB' ? 'USB Camera' : 'Screen Share'}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2 shrink-0">
              <Button
                variant="secondary"
                size="sm"
                onClick={handleSaveCrop}
                className="flex items-center gap-1.5 text-xs sm:text-sm px-2 sm:px-3"
              >
                <FaSave className="w-3 h-3" />
                <span className="hidden sm:inline">Save</span>
              </Button>

              <Button
                variant="primary"
                size="sm"
                onClick={handleStartNow}
                className="flex items-center gap-1.5 text-xs sm:text-sm px-3 sm:px-4"
              >
                <FaPlay className="w-3 h-3" />
                Start Now
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeedPreviewModal;
